import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { NotFound } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { BaseBanner } from '@factorialco/f0-react/dist/experimental'
import {
  CanSeeModulesMarketplaceDocument,
  CanSelfUpsellDocument,
  GetFeatureAvailabilitiesDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { locale } from 'modules/marketplace/components/utils/locales'
import OneBanner from 'modules/marketplace_modules/components/OneBanner'
import { ModuleCarousel } from 'modules/marketplace_modules/components/Revamp/Carousel'
import ClientReviews from 'modules/marketplace_modules/components/Revamp/ClientReviews'
import FeatureBlocks from 'modules/marketplace_modules/components/Revamp/FeatureBlocks'
import LatestUpdates from 'modules/marketplace_modules/components/Revamp/LatestUpdates'
import PricingPlan from 'modules/marketplace_modules/components/Revamp/PricingPlan'
import SidePanel from 'modules/marketplace_modules/components/Revamp/SidePanel'
import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { SelfUpsellProvider } from 'modules/upsell/store/SelfUpsellProvider'

const ModulesDetailRevamp = () => {
  const { id: slug } = useParams()
  const {
    company,
    access: { locale: user_locale },
  } = useCurrentSession()
  const { moduleAvailabilities } = useModuleAvailabilities()
  const { data: policies } = useQuery(CanSeeModulesMarketplaceDocument)
  const { data: selfUpsellPolicies } = useQuery(CanSelfUpsellDocument)
  const datoFormatLocale = locale[user_locale]
  const countryCodeForCompany = countryCode(company.legalEntities[0]?.country || '')
  const isAuthorized =
    policies?.modules._policies.moduleManageModule.can && !company.subscription.inFreeTrial

  const canSeeSelfUpsell = selfUpsellPolicies?.billing._policies.subscriptionUpgradeSubscription.can

  const { data: moduleData, loading } = useGetDatoModules({
    locale: datoFormatLocale,
    slug: slug,
    countryCode: countryCodeForCompany,
  })

  const { data: featureData } = useQuery(GetFeatureAvailabilitiesDocument, {
    variables: {
      modules: [],
      ids: ['one_core'],
    },
    fetchPolicy: 'cache-first',
  })

  const isAlreadyInstalled = moduleAvailabilities.find(
    (module: ModuleAvailabilityType) => module.id === moduleData[0]?.chargebeeModuleName
  )?.available

  useEffect(() => {
    if (canSeeSelfUpsell && isAuthorized && !isAlreadyInstalled) {
      track(trackingEvents.marketplaceModules.viewSelfUpsellButton, {
        module: module?.chargebeeModuleName,
        from: 'marketplace_resource_header',
      })
    }
  }, [])

  if (loading || moduleData.length === 0) return <Loading />

  const module = moduleData.at(0)

  if (!module) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  const crossSellingAddonReference = module?.crossSellingBillingAddonReference || []

  const crossSellingAlreadyInstalled =
    featureData?.companies?.featureAvailabilities.find((node) => node.id === 'one_core')
      ?.available ?? false

  const shouldShowOneWidget =
    crossSellingAddonReference &&
    crossSellingAddonReference.length > 0 &&
    !crossSellingAlreadyInstalled &&
    !isAlreadyInstalled

  return (
    <SelfUpsellProvider
      availableAddons={module?.billingAddonReference || []}
      availableCrossSellingAddons={module?.crossSellingBillingAddonReference || []}
      moduleName={module?.chargebeeModuleName || ''}
    >
      <ResponsivePage.Body variant="fullscreen">
        <TwoColumnLayout
          sideContent={
            <SidePanel
              pitchDeck={module?.downloadInfo?.url ? [{ url: module.downloadInfo.url }] : undefined}
              module={module?.slug || undefined}
              isAuthorized={isAuthorized}
              setupGuide={module?.setupGuide}
              benefit={module?.benefit}
              moduleName={module?.name || ''}
              navatticImage={module?.navatticImage?.url || module?.carousel?.[0]?.url || ''}
              navatticUrl={module?.navatticUrl || ''}
              navatticTitle={module?.navatticTitle || ''}
              moduleSlug={module?.slug || ''}
            />
          }
        >
          {module?.bannerCallout && (
            <Box paddingBottom={'s40'}>
              <BaseBanner
                title={module?.bannerCallout?.title || ''}
                subtitle={module?.bannerCallout?.description || ''}
                mediaUrl={module?.bannerCallout?.image?.url || ''}
              />
            </Box>
          )}

          {shouldShowOneWidget && (
            <Box
              paddingBottom={'s40'}
              style={{ marginTop: '-30px', marginLeft: '-40px', marginRight: '-40px' }}
            >
              <OneBanner
                chargebeeModuleName={module?.chargebeeModuleName || ''}
                moduleName={module?.translatedName || ''}
              />
            </Box>
          )}

          <ModuleCarousel
            video={module?.externalUrl}
            images={module?.carousel || []}
            moduleSlug={module?.slug || ''}
          />
          <FeatureBlocks
            features={module?.values || []}
            module={module?.translatedName || ''}
            pitchDeck={module?.downloadInfo?.url ? [{ url: module.downloadInfo.url }] : undefined}
          />
          <PricingPlan
            module={module?.slug || undefined}
            isAuthorized={isAuthorized}
            chargebeeModuleName={module?.chargebeeModuleName || ''}
          />
          <ClientReviews reviews={module?.testimonials} />
          <LatestUpdates module={module.chargebeeModuleName || ''} moduleName={module.name || ''} />
        </TwoColumnLayout>
      </ResponsivePage.Body>
    </SelfUpsellProvider>
  )
}

export default ModulesDetailRevamp
